import type { ReportSection } from '../ReportSection.js'
import type { ClusteringMethod } from '../../../../../Model/Order/processingProfile.js'
import { SWARM_SEQUENCE_THRESHOLD } from '../../../../../Model/Order/processingProfile.js'
import { Block, Caveat, Code, num, Prose, Step, Title } from './primitives.js'
import { flaggedSampleLabel, flaggedUnassignedSamples, UNASSIGNED_READS_WARN_PERCENT } from '../../../../../Model/Order/unassignedReads.js'

export const dereplication: ReportSection = {
  id: 'dereplication',
  applies: () => true,
  Render: ({ data: { profile, stats, postprocessingData } }) => {
    const unit = profile.consensusApplied ? 'consensus sequences' : 'unique sequences'
    return (
      <Block>
        <Title>Dereplication</Title>
        <Prose>
          <p className="mb-2">
            {profile.singletonFilter
              ? 'We filtered for unique sequences across all samples and filtered out dataset-wide singletons.'
              : 'We filtered for unique sequences across all samples. Consensus sequences are already error-corrected, so no singleton filter was applied.'}
          </p>
        </Prose>
        <Step title="Dereplication">
          <p className="mb-1">
            In all samples, we had <strong>{num(stats.uniqueSequences)}</strong> {unit}.
          </p>
          <p>
            After dereplication, a total of <strong>{num(postprocessingData.dereplicateFinalSequences)}</strong> unique
            {profile.singletonFilter ? ', non-singleton' : ''} sequences were kept.
          </p>
        </Step>
      </Block>
    )
  }
}

export const preclusteringChimera: ReportSection = {
  id: 'preclusteringChimera',
  applies: () => true,
  Render: ({ data: { postprocessingData } }) => (
    <Block>
      <Title>Pre-clustering and de novo chimera detection</Title>
      <Step title="Pre-clustering">
        <p>
          Pre-clustering was performed before chimera detection using vsearch 2.28.1 with the parameters:{' '}
          <Code>-cluster_size -id 0.98 -centroids</Code>, resulting in a total of{' '}
          <strong>{num(postprocessingData.allPreclusteredOtusCount)}</strong> clusters.
        </p>
      </Step>
      <Step title="Chimera detection">
        <p className="mb-1">
          Chimeras were detected using vsearch 2.28.1 with the parameters: <Code>-uchime_denovo -nonchimeras</Code>.
        </p>
        <p>
          <strong>{num(postprocessingData.chimeraCount)}</strong> sequences were flagged as chimeric and removed;{' '}
          <strong>{num(postprocessingData.nonChimeraCount)}</strong> were kept (
          <strong>{num(postprocessingData.abundanceNonChimeraCount)}</strong> reads).
        </p>
      </Step>
    </Block>
  )
}

const CLUSTERING_PROSE: Record<ClusteringMethod, { tool: string; params: string }> = {
  swarm: { tool: 'SWARM 3.1.5', params: '-d 12' },
  vsearch: { tool: 'vsearch 2.28.1', params: '-cluster_size -id 0.97 -strand both' }
}

export const clustering: ReportSection = {
  id: 'clustering',
  applies: () => true,
  Render: ({ data: { profile, postprocessingData } }) => {
    const prose = CLUSTERING_PROSE[profile.clusteringMethod]
    return (
      <Block>
        <Title>Final clustering</Title>
        <Step title="Final clustering">
          <p className="mb-1">
            Final clustering was performed using {prose.tool} with the parameters: <Code>{prose.params}</Code>. Cluster representatives were
            sorted and an OTU table was created using the vsearch <Code>-usearch_global</Code> utility.
          </p>
          {profile.caveats.includes('CLUSTERING_INFERRED') && profile.clusteringMethod === 'swarm' && (
            <Caveat>
              Runs above {num(SWARM_SEQUENCE_THRESHOLD)} sequences fall back to vsearch; this run did not record which was used.
            </Caveat>
          )}
          <p>
            We ended up with <strong>{num(postprocessingData.allOtusCount)}</strong> OTUs.
          </p>
        </Step>
      </Block>
    )
  }
}

export const taxonomy: ReportSection = {
  id: 'taxonomy',
  applies: () => true,
  Render: ({ data: { postprocessingData, stats, unassignedSamples } }) => {
    const flagged = flaggedUnassignedSamples(unassignedSamples)
    return (
      <Block>
        <Title>Taxonomic assignment</Title>
        <Step title="BLAST and consensus taxonomy">
          <p className="mb-1">
            <strong>{num(postprocessingData.finalClusteringBoldOtusCount)}</strong> of the{' '}
            <strong>{num(postprocessingData.allOtusCount)}</strong> OTUs found BLAST matches following the negative-control-based filtering
            procedures. By consensus, <strong>{num(postprocessingData.familyCountOtus)}</strong> were identified to the family,{' '}
            <strong>{num(postprocessingData.genusCountOtus)}</strong> to the genus, and{' '}
            <strong>{num(postprocessingData.speciesCountOtus)}</strong> to the species level.
          </p>
          {stats.unassignedReadsTotal != null && (
            <p>
              <strong>{num(stats.unassignedReadsTotal)}</strong> reads belong to OTUs without a valid BLAST hit and so carry no taxonomic
              assignment. The per-sample table lists their share of the reads of each sample.
            </p>
          )}
          {flagged.length > 0 && (
            <div data-testid="summary-unassigned-warning" className="mt-2 border-l-4 border-amber-500 bg-amber-50 p-2 text-sm">
              <p className="font-semibold">
                In {flagged.length} {flagged.length === 1 ? 'sample' : 'samples'}, {UNASSIGNED_READS_WARN_PERCENT} % or more of the reads
                belong to OTUs without a valid BLAST hit:
              </p>
              <p>{flagged.map(flaggedSampleLabel).join(', ')}</p>
            </div>
          )}
        </Step>
      </Block>
    )
  }
}
